
import React from 'react';
import { format } from 'date-fns';
import { AdminAction } from '@/services/db/admin/adminActionTracking';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Shield, Settings, User, AlertTriangle } from 'lucide-react';

interface AdminActionHistoryProps {
  actions: AdminAction[];
  title?: string;
}

const AdminActionHistory: React.FC<AdminActionHistoryProps> = ({ 
  actions,
  title = "Administrative Actions"
}) => {
  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'student_record':
        return <User className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      case 'permission':
        return <Shield className="h-4 w-4" />;
      case 'system_setting':
        return <Settings className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'student_record':
        return 'bg-blue-100 text-blue-800';
      case 'document':
        return 'bg-green-100 text-green-800';
      case 'permission':
        return 'bg-yellow-100 text-yellow-800';
      case 'system_setting':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDetails = (details: Record<string, any>) => {
    return Object.entries(details).map(([key, value]) => {
      // Format arrays and objects
      const displayValue = typeof value === 'object' && value !== null 
        ? JSON.stringify(value) 
        : String(value);
        
      return (
        <div key={key} className="grid grid-cols-2 gap-2">
          <span className="text-muted-foreground capitalize">
            {key.replace(/_/g, ' ')}:
          </span>
          <span className="truncate">
            {displayValue}
          </span>
        </div>
      );
    });
  };

  if (!actions || actions.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground flex flex-col items-center justify-center py-8">
            <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <div>No administrative actions recorded</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[400px]">
          <div className="space-y-4">
            {actions.map((action) => (
              <div key={action.id} className="flex gap-4 items-start border-b pb-4 last:border-0">
                <div className="mt-1">
                  {getActionIcon(action.actionType)}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{action.description}</p>
                      <p className="text-sm text-muted-foreground">
                        By {action.performedBy}
                      </p>
                      <Badge 
                        className={`mt-1 ${getActionColor(action.actionType)}`}
                        variant="outline"
                      >
                        {action.actionType.replace('_', ' ')}
                      </Badge>
                    </div>
                    <time className="text-sm text-muted-foreground">
                      {format(new Date(action.timestamp), 'PPp')}
                    </time>
                  </div>
                  {Object.keys(action.details).length > 0 && (
                    <div className="mt-2 text-sm bg-muted/50 rounded-md p-2">
                      {formatDetails(action.details)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default AdminActionHistory;
